import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Brand } from '../../components/Brand.js';
import { apiRequest } from '../../lib/api.js';
import { useNoIndex } from '../../lib/useNoIndex.js';

type State = 'working' | 'done' | 'failed';

/**
 * The page behind the "Stop these emails" link in every lifecycle email.
 *
 * Outside every auth gate, and it has to be: the link is followed from a mail
 * client, often on a phone that has never signed in. The capability is the
 * token in the URL, which is why the request is `anonymous`.
 *
 * It unsubscribes on mount rather than behind a confirm button. The large
 * mailbox providers call this same endpoint themselves via
 * `List-Unsubscribe-Post`, so it must work with no interaction at all, and a
 * page that then asked a human to click again would be the odd one out. There
 * is nothing destructive to guard: re-subscribing is signing in and asking.
 */
export function Unsubscribe() {
  // Never indexed. The URL only does anything with a per-user token on it, so
  // an indexed copy would be a page a crawler cannot use and a searcher should
  // not find — the same reasoning the audience routes carry the tag for.
  useNoIndex();

  const [params] = useSearchParams();
  const [state, setState] = useState<State>('working');

  const userId = params.get('u') ?? '';
  const token = params.get('t') ?? '';

  useEffect(() => {
    if (!userId || !token) {
      setState('failed');
      return;
    }

    let cancelled = false;
    apiRequest<{ stopped?: boolean }>('/lifecycle/unsubscribe', {
      method: 'POST',
      body: { userId, token },
      anonymous: true,
    })
      .then((result) => {
        // A 200 is not success on its own. Mail clients truncate long links,
        // so a token that does not match is a case real people hit — and
        // claiming they are unsubscribed when they are not is how somebody
        // keeps getting mail they asked to stop.
        if (!cancelled) setState(result?.stopped ? 'done' : 'failed');
      })
      .catch(() => {
        if (!cancelled) setState('failed');
      });

    return () => {
      cancelled = true;
    };
  }, [userId, token]);

  return (
    <div className="legal-page">
      <header className="legal-head">
        <Brand to="/" />
      </header>

      <main className="legal">
        {state === 'working' ? <h1>One moment</h1> : null}

        {state === 'done' ? (
          <>
            <h1>You are unsubscribed</h1>
            <p>
              We will not send you any more of the getting-started emails. You will still get the
              messages the service depends on — verification codes, password resets, workspace
              invitations, and any notice we owe you before a change to your plan.
            </p>
            <p>Nothing about your account or your polls has changed.</p>
          </>
        ) : null}

        {state === 'failed' ? (
          <>
            <h1>That link did not work</h1>
            <p>
              It may have been shortened or cut in half by a mail client. Email{' '}
              <a href="mailto:info@bluualpha.com">info@bluualpha.com</a> and we will take you off
              the list by hand.
            </p>
          </>
        ) : null}
      </main>

      <footer className="legal-foot">
        <Link to="/">Back to FloorPoll</Link>
      </footer>
    </div>
  );
}
